// Desktop Dev Chat Interface
// Three-section layout: Header, Messages, Footer
// Fills available vertical and horizontal space; no max-width (parent constrains)

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { DesktopDevChatHeader } from './DesktopDevChatHeader';
import { DesktopDevChatMessages } from './DesktopDevChatMessages';
import { RevoltButton, RevoltIcon, RevoltTextarea } from '../../../../../components';
import { DesktopDevChatToolToggle } from './DesktopDevChatToolToggle';
import './DesktopDevChatInterface.css';

export function DesktopDevChatInterface({
  messages = [],
  isGenerating = false,
  messageQueue = [],
  onRemoveFromQueue,
  onCancelGenerate,
  chats = [],
  activeChatId,
  onSelectChat,
  onCreateChat,
  onDeleteChat,
  models,
  selectedModelId,
  modelState,
  loadProgress,
  statusMessage,
  statusType,
  contextRuntimeTokens = null,
  contextTrainTokens = null,
  layerGpuCount = null,
  layerTotalCount = null,
  onSelectModel,
  onLoad,
  onUnload,
  contextSizeMode,
  contextSizeMin,
  contextSizeMax,
  contextAllocationMode,
  contextAllocationSize,
  onContextSizeModeChange,
  onContextSizeMinChange,
  onContextSizeMaxChange,
  onContextAllocationModeChange,
  onContextAllocationSizeChange,
  defaultContextFlashAttention = false,
  onDefaultContextFlashAttentionChange,
  onContextOptionsCommit,
  devChatToolDefinitions = [],
  devChatToolsEnabled = {},
  onDevChatToolToggle,
  onSendMessage,
  devChatManager,
  onToolInteractionAction,
  showSystemPromptEditor = false,
  systemPrompt = '',
  onSystemPromptChange,
}) {
  const [inputValue, setInputValue] = useState('');
  const [queueExpanded, setQueueExpanded] = useState(true);
  const [clipboardHint, setClipboardHint] = useState('');
  const messagesMountRef = useRef(null);
  const clipboardHintTimerRef = useRef(null);

  const showClipboardHint = useCallback((message) => {
    if (clipboardHintTimerRef.current) {
      clearTimeout(clipboardHintTimerRef.current);
    }
    setClipboardHint(message);
    clipboardHintTimerRef.current = setTimeout(() => {
      setClipboardHint('');
      clipboardHintTimerRef.current = null;
    }, 4500);
  }, []);

  useEffect(() => {
    return () => {
      if (clipboardHintTimerRef.current) {
        clearTimeout(clipboardHintTimerRef.current);
      }
    };
  }, []);

  const copyConversationToClipboard = useCallback(async () => {
    let sessionLines = ['--- Llama getChatHistory() ---', '(not requested: no devChatManager.getChatHistory)'];
    if (devChatManager && typeof devChatManager.getChatHistory === 'function') {
      try {
        const res = await devChatManager.getChatHistory();
        sessionLines = [
          '--- Llama getChatHistory() full JSON ---',
          safeJsonStringify(res),
          '',
          '--- Session history array only (JSON) ---',
          res?.history != null ? safeJsonStringify(res.history) : '(history field missing or null)',
        ];
      } catch (e) {
        sessionLines = [
          '--- Llama getChatHistory() ERROR ---',
          e?.message || String(e),
        ];
      }
    }

    const text = [
      bannerLine('Dev Chat — conversation state (plain text, paste-friendly)'),
      `Timestamp: ${new Date().toISOString()}`,
      '',
      '--- UI messages (React state, JSON) ---',
      safeJsonStringify(messages),
      '',
      ...sessionLines,
      '',
      bannerLine('end'),
    ].join('\n');

    const result = await copyPlainTextToClipboard(text);
    if (result.ok) {
      showClipboardHint(`Copied chat state (${text.length} chars) — paste anywhere.`);
    } else {
      showClipboardHint(`Copy failed: ${result.error || 'unknown'} — see console.`);
      console.error('[DevChat] Clipboard:', result.error, '\n--- dump below ---\n', text);
    }
  }, [messages, devChatManager, showClipboardHint]);

  const copyDomBubblesToClipboard = useCallback(async () => {
    const root = messagesMountRef.current;
    if (!root) {
      showClipboardHint('Copy failed: messages area not mounted.');
      return;
    }
    const rows = collectDevChatDomBubbleRows(root);
    const text = [
      bannerLine('Dev Chat — DOM bubble snapshot (plain text, paste-friendly)'),
      `Timestamp: ${new Date().toISOString()}`,
      `Row count: ${rows.length}`,
      `Empty (no header + no body text): ${rows.filter((r) => r.isEmpty).length}`,
      '',
      ...formatDomBubbleRowsAsText(rows),
      '',
      '--- Rows as JSON (full detail) ---',
      safeJsonStringify(rows),
      '',
      bannerLine('end'),
    ].join('\n');

    const result = await copyPlainTextToClipboard(text);
    if (result.ok) {
      showClipboardHint(`Copied bubble snapshot (${text.length} chars) — paste anywhere.`);
    } else {
      showClipboardHint(`Copy failed: ${result.error || 'unknown'} — see console.`);
      console.error('[DevChat] Clipboard:', result.error, '\n--- dump below ---\n', text);
    }
  }, [showClipboardHint]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;
    const success = await onSendMessage?.(inputValue.trim());
    if (success) setInputValue('');
  };

  const hasQueue = messageQueue.length > 0;

  return (
    <div className="desktop-dev-chat-interface">
      <header className="desktop-dev-chat-header-wrapper">
        <DesktopDevChatHeader
          chats={chats}
          activeChatId={activeChatId}
          onSelectChat={onSelectChat}
          onCreateChat={onCreateChat}
          onDeleteChat={onDeleteChat}
          models={models}
          selectedModelId={selectedModelId}
          modelState={modelState}
          loadProgress={loadProgress}
          statusMessage={statusMessage}
          statusType={statusType}
          contextRuntimeTokens={contextRuntimeTokens}
          contextTrainTokens={contextTrainTokens}
          layerGpuCount={layerGpuCount}
          layerTotalCount={layerTotalCount}
          onSelectModel={onSelectModel}
          onLoad={onLoad}
          onUnload={onUnload}
          contextSizeMode={contextSizeMode}
          contextSizeMin={contextSizeMin}
          contextSizeMax={contextSizeMax}
          contextAllocationMode={contextAllocationMode}
          contextAllocationSize={contextAllocationSize}
          onContextSizeModeChange={onContextSizeModeChange}
          onContextSizeMinChange={onContextSizeMinChange}
          onContextSizeMaxChange={onContextSizeMaxChange}
          onContextAllocationModeChange={onContextAllocationModeChange}
          onContextAllocationSizeChange={onContextAllocationSizeChange}
          defaultContextFlashAttention={defaultContextFlashAttention}
          onDefaultContextFlashAttentionChange={onDefaultContextFlashAttentionChange}
          onContextOptionsCommit={onContextOptionsCommit}
        />
      </header>

      <div ref={messagesMountRef} className="desktop-dev-chat-messages">
        <DesktopDevChatMessages
          messages={messages}
          onToolInteractionAction={onToolInteractionAction}
        />
      </div>

      <footer className="desktop-dev-chat-footer">
        {hasQueue && (
          <div className={`desktop-dev-chat-queue ${queueExpanded ? 'desktop-dev-chat-queue--expanded' : ''}`}>
            <button
              type="button"
              className="desktop-dev-chat-queue__header"
              onClick={() => setQueueExpanded((prev) => !prev)}
              aria-expanded={queueExpanded}
            >
              <i className={`fa-solid fa-chevron-${queueExpanded ? 'down' : 'right'} desktop-dev-chat-queue__chevron`} aria-hidden />
              <span className="desktop-dev-chat-queue__count">
                {messageQueue.length} {messageQueue.length === 1 ? 'message' : 'messages'} queued
              </span>
            </button>
            {queueExpanded && (
              <ul className="desktop-dev-chat-queue__list">
                {messageQueue.map((msg, i) => (
                  <li key={i} className="desktop-dev-chat-queue__item">
                    <span className="desktop-dev-chat-queue__preview">{msg.length > 60 ? `${msg.slice(0, 60)}…` : msg}</span>
                    <button
                      type="button"
                      className="desktop-dev-chat-queue__remove"
                      onClick={() => onRemoveFromQueue?.(i)}
                      title="Remove from queue"
                      aria-label={`Remove message ${i + 1} from queue`}
                    >
                      <i className="fa-solid fa-times" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {showSystemPromptEditor && (
          <div className="desktop-dev-chat-system-prompt-editor">
            <div className="desktop-dev-chat-system-prompt-editor__label">
              System prompt
            </div>
            <RevoltTextarea
              value={systemPrompt}
              onChange={(e) => onSystemPromptChange?.(e.target.value)}
              className="desktop-dev-chat-system-prompt-editor__textarea"
              placeholder="(Leave empty to use the model's default system prompt.)"
              rows={4}
              minRows={4}
              maxRows={10}
              autoResize={true}
            />
          </div>
        )}

        <div className="desktop-dev-chat-footer__input-row">
          <RevoltTextarea
            className="desktop-dev-chat-footer__textarea"
            placeholder="Type a message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onSubmit={handleSend}
            rows={2}
            minRows={2}
            maxRows={5}
            autoResize={true}
          />
          <RevoltButton
            key={isGenerating ? 'stop' : 'send'}
            variant={isGenerating ? 'outline' : 'primary'}
            size="default"
            onClick={isGenerating ? onCancelGenerate : handleSend}
            disabled={!isGenerating && (!selectedModelId || !inputValue.trim())}
            className="desktop-dev-chat-footer__send-btn"
            title={isGenerating ? 'Stop generation' : 'Send'}
            aria-label={isGenerating ? 'Stop generation' : 'Send'}
          >
            <RevoltIcon name={isGenerating ? 'square' : 'send'} size="sm" />
          </RevoltButton>
        </div>
        <div className="desktop-dev-chat-footer__debug-row">
          <RevoltButton
            variant="outline"
            size="sm"
            className="desktop-dev-chat-footer__debug-btn"
            title="Copies React messages + getChatHistory() as plain text to the clipboard"
            onClick={() => copyConversationToClipboard()}
          >
            Copy chat state
          </RevoltButton>
          <RevoltButton
            variant="outline"
            size="sm"
            className="desktop-dev-chat-footer__debug-btn"
            title="Copies each UI message row (DOM) as plain text + JSON to the clipboard"
            onClick={copyDomBubblesToClipboard}
          >
            Copy bubbles (DOM)
          </RevoltButton>
          {clipboardHint ? (
            <span className="desktop-dev-chat-footer__debug-hint" role="status">
              {clipboardHint}
            </span>
          ) : null}
        </div>
        <div className="desktop-dev-chat-footer__tools-row">
          <span className="desktop-dev-chat-footer__tools-label">Tools</span>
          {devChatToolDefinitions.length === 0 ? (
            <span className="desktop-dev-chat-footer__tools-placeholder">
              No tools exposed (load Dev Chat in desktop with llamaCppDev API)
            </span>
          ) : (
            <div className="desktop-dev-chat-footer__tools-list">
              {devChatToolDefinitions.map((def) => {
                const id = def?.name;
                if (!id) return null;
                const checked = devChatToolsEnabled[id] !== false;
                return (
                  <div key={id} className="desktop-dev-chat-footer__tool-chip">
                    <DesktopDevChatToolToggle
                      toolId={id}
                      description={def.description}
                      active={checked}
                      disabled={isGenerating}
                      onToggle={(next) => onDevChatToolToggle?.(id, next)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

function bannerLine(title) {
  const line = '='.repeat(72);
  return `${line}\n${title}\n${line}`;
}

function safeJsonStringify(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return `[JSON.stringify error: ${e?.message || e}]`;
  }
}

/**
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
async function copyPlainTextToClipboard(text) {
  if (typeof text !== 'string') {
    return { ok: false, error: 'internal: not a string' };
  }
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return { ok: true };
    } catch {
      // Fall back to execCommand (e.g. permissions / non-secure context quirks).
    }
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok ? { ok: true } : { ok: false, error: 'execCommand(copy) returned false' };
  } catch (e) {
    return { ok: false, error: e?.message || String(e) };
  }
}

/** @param {Array<Record<string, unknown>>} rows */
function formatDomBubbleRowsAsText(rows) {
  return rows.map((r) => {
    const idx = r.index;
    const kind = r.kind ?? '';
    const lines = [`### Row ${idx}  [${kind}]`];
    if (r.role != null) lines.push(`role: ${r.role}`);
    if (r.presetHint != null && r.presetHint !== '') lines.push(`preset: ${r.presetHint}`);
    if (r.note != null) lines.push(`note: ${r.note}`);
    lines.push(`isEmpty: ${r.isEmpty === true}`);
    const header = r.headerText != null ? String(r.headerText) : '';
    const body = r.bodyText != null ? String(r.bodyText) : '';
    lines.push(`header: ${header ? header : '(none)'}`);
    lines.push('body:');
    lines.push(body ? body : '(empty)');
    if (kind === 'error' && r.stackText != null && String(r.stackText).length > 0) {
      lines.push('stack:');
      lines.push(String(r.stackText));
    }
    return lines.join('\n');
  });
}

/**
 * Snapshot of each visual row under the messages list (matches React map order).
 * @param {HTMLElement} rootEl — element that contains `.desktop-dev-chat-messages-inner`
 */
export function collectDevChatDomBubbleRows(rootEl) {
  const inner = rootEl.querySelector('.desktop-dev-chat-messages-inner');
  if (!inner) {
    return [];
  }
  /** @type {Array<Record<string, unknown>>} */
  const rows = [];
  const children = Array.from(inner.children);
  children.forEach((wrapper, index) => {
    if (wrapper.classList.contains('desktop-dev-chat-messages-empty')) {
      const label = wrapper.textContent?.trim() ?? '';
      rows.push({
        index,
        kind: 'empty_placeholder',
        headerText: '',
        bodyText: label,
        isEmpty: !label,
      });
      return;
    }

    const errBubble = wrapper.querySelector('.desktop-dev-chat-error-bubble');
    if (errBubble) {
      const msg =
        wrapper.querySelector('.desktop-dev-chat-error-message')?.textContent?.trim() ?? '';
      const stack =
        wrapper.querySelector('.desktop-dev-chat-error-stack')?.textContent?.trim() ?? '';
      const bodyText = msg || (stack.length > 0 ? `(see stack below, ${stack.length} chars)` : '');
      rows.push({
        index,
        kind: 'error',
        role: 'assistant',
        presetHint: 'desktop-dev-chat-error-bubble',
        headerText: '',
        bodyText,
        stackText: stack,
        stackLength: stack.length,
        isEmpty: !msg && !stack,
      });
      return;
    }

    const bubbleRoot = wrapper.querySelector('.chat-bubble');
    if (!bubbleRoot) {
      rows.push({
        index,
        kind: 'unknown_wrapper',
        note: 'no .chat-bubble',
        isEmpty: true,
      });
      return;
    }

    const innerBubble = bubbleRoot.querySelector('.chat-bubble-bubble');
    const presetHints =
      innerBubble != null
        ? [...innerBubble.classList].filter(
            (c) =>
              c.startsWith('chat-bubble-') &&
              c !== 'chat-bubble-bubble' &&
              c !== 'chat-bubble-bubble-user' &&
              c !== 'chat-bubble-bubble-assistant'
          )
        : [];

    const headerEl = bubbleRoot.querySelector(
      '.thinking-header, .function-call-header, .chat-bubble-header'
    );
    const headerText = headerEl?.innerText?.replace(/\s+/g, ' ')?.trim() ?? '';

    const contentEl = bubbleRoot.querySelector('.chat-bubble-bubble-content');
    const bodyText = contentEl?.innerText?.trim() ?? '';

    const role = bubbleRoot.classList.contains('chat-bubble-user') ? 'user' : 'assistant';

    rows.push({
      index,
      kind: 'chat_bubble',
      role,
      presetHint: presetHints.length > 0 ? presetHints.join(' ') : null,
      headerText,
      bodyText,
      bodyLength: bodyText.length,
      isEmpty: !headerText && !bodyText,
    });
  });
  return rows;
}
